import FeatureGates from '@atlaskit/feature-gate-js-client';

import { wacRefreshJsm } from '@/utils/experiment/wac-refresh-experiments';

export const JSM_PLAYGROUND_SIGNUP_SOURCE = 'playground';
export const JSM_PLAYGROUND_SIGNUP_INTENT_CHANGE_EVENT = 'jsm-playground-signup-intent-change';

export const JSM_PLAYGROUND_TEAM_TYPES = [
	'it-support',
	'operations',
	'software-development',
	'human-resources',
] as const;

export type JsmPlaygroundTeamType = (typeof JSM_PLAYGROUND_TEAM_TYPES)[number];

export type JsmPlaygroundSignupIntent = {
	signupSource: typeof JSM_PLAYGROUND_SIGNUP_SOURCE;
	teamType: JsmPlaygroundTeamType;
};

/**
 * Reads the user's allocation for the playground experiment without evaluating
 * its cohort or firing an exposure.
 *
 * The helpers in this file run from the signup submit handler and URL builders
 * as well as during render. `getExperiment(wacRefreshJsm)` evaluates the
 * experiment's cohort, which calls `useLocation()`; outside render that throws
 * and left the signup button stuck in its submitting state. Cohort targeting
 * and exposures are owned by `useExperiment(wacRefreshJsm)` on the JSM page,
 * the only place the intent is written to the URL. Downstream only the
 * allocation matters: it stops a disabled experiment from honouring stale
 * intent carried in shared or emailed continue URLs.
 */
export const isJsmPlaygroundSignupIntentEnabled = (): boolean =>
	// eslint-disable-next-line local/no-get-experiment-value-eslint -- allocation read outside render, see above
	FeatureGates.getExperimentValue(
		wacRefreshJsm.experimentName,
		wacRefreshJsm.parameterName,
		wacRefreshJsm.defaultValue,
		{ fireExperimentExposure: false, typeGuard: wacRefreshJsm.options.typeGuard },
	);

export const isJsmPlaygroundSignup = (
	search: string | URLSearchParams | undefined,
	isEnabled?: boolean,
): boolean => {
	if (!search) {
		return false;
	}

	const searchParameters = typeof search === 'string' ? new URLSearchParams(search) : search;
	if (searchParameters.get('signupSource') !== JSM_PLAYGROUND_SIGNUP_SOURCE) {
		return false;
	}

	return isEnabled ?? isJsmPlaygroundSignupIntentEnabled();
};

export const getJsmPlaygroundSignupIntent = (
	search: string | URLSearchParams | undefined,
	isEnabled?: boolean,
): JsmPlaygroundSignupIntent | undefined => {
	if (!search) {
		return undefined;
	}

	const searchParameters = typeof search === 'string' ? new URLSearchParams(search) : search;
	const signupSource = searchParameters.get('signupSource');
	const teamType = searchParameters.get('team_type');

	if (
		signupSource !== JSM_PLAYGROUND_SIGNUP_SOURCE ||
		!JSM_PLAYGROUND_TEAM_TYPES.includes(teamType as JsmPlaygroundTeamType)
	) {
		return undefined;
	}

	if (!(isEnabled ?? isJsmPlaygroundSignupIntentEnabled())) {
		return undefined;
	}

	return {
		signupSource,
		teamType: teamType as JsmPlaygroundTeamType,
	};
};

export const setJsmPlaygroundSignupIntent = (
	searchParameters: URLSearchParams,
	teamType: JsmPlaygroundTeamType,
) => {
	searchParameters.set('signupSource', JSM_PLAYGROUND_SIGNUP_SOURCE);
	searchParameters.set('team_type', teamType);
	searchParameters.delete('templateId');
};

const isJsmSignupPath = (pathname: string) =>
	pathname.endsWith('/try/cloud/signup') ||
	pathname.endsWith('/software/jira/service-management/free');

export const addJsmPlaygroundSignupIntentToHref = (
	href: string,
	currentSearch: string,
	origin = 'https://www.atlassian.com',
	isEnabled?: boolean,
): string => {
	const intent = getJsmPlaygroundSignupIntent(currentSearch, isEnabled);
	if (!intent) {
		return href;
	}

	const targetUrl = new URL(href, origin);
	if (!isJsmSignupPath(targetUrl.pathname)) {
		return href;
	}

	setJsmPlaygroundSignupIntent(targetUrl.searchParams, intent.teamType);

	return href.startsWith('/')
		? `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
		: targetUrl.toString();
};
